import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SeatingMove, SeatingParty, SeatingState } from '@peles/shared';
import { useArrangeSeats, useMoveSeats, usePinSeats } from '../../hooks/useSeating';
import { useDialog } from '../../context/DialogContext';
import { extractApiErrorCode, extractApiErrorMessage } from '../../utils/apiError';
import { isOverlayOpen, isTypingTarget } from '../command/shortcuts';
import type { SeatingArrangement } from '../../api/seating';
import { useSeatingHistory } from './useSeatingHistory';
import { partyIndex, snapshotOf, tableTitle } from './boardModel';

export interface SeatingNotice {
  text: string;
  tone: 'info' | 'error';
  undo?: boolean;
}

/**
 * Changing who sits where on one plan, the same way wherever it is done — the
 * seating board and the floor plan's Seating mode both use it. Every change is
 * written at once and recorded so it can be undone (its undo puts back where
 * the parties it touched sat); a full table asks before squeezing a party in;
 * what happened, or why it did not, is the `notice`.
 */
export function useSeatingActions(eventId: string, live: SeatingState | undefined) {
  const moveSeats = useMoveSeats(eventId);
  const arrangeSeats = useArrangeSeats(eventId);
  const pinSeats = usePinSeats(eventId);
  const { confirm } = useDialog();
  const history = useSeatingHistory();
  const [notice, setNotice] = useState<SeatingNotice | null>(null);
  const floorPlanId = live?.plan.id ?? null;
  const parties = useMemo(
    () => (live ? partyIndex(live) : new Map<string, SeatingParty>()),
    [live],
  );
  const busy = moveSeats.isPending || arrangeSeats.isPending || pinSeats.isPending || history.busy;

  const fail = useCallback(
    (err: unknown, fallback: string) =>
      setNotice({ text: extractApiErrorMessage(err, fallback), tone: 'error' }),
    [],
  );

  /** Write moves, asking before squeezing a party onto a full table; undoable. */
  const runMoves = useCallback(
    async (moves: SeatingMove[], label: string) => {
      if (!live || !floorPlanId) return;
      const before = snapshotOf(live, [...new Set(moves.map((m) => m.guestId))]);
      let allowOverCapacity = false;
      const send = () =>
        moveSeats.mutateAsync({
          floorPlanId,
          moves,
          ...(allowOverCapacity ? { allowOverCapacity } : {}),
        });
      try {
        await send();
      } catch (err) {
        const full = extractApiErrorCode(err) === 'OVER_CAPACITY';
        const retry =
          full &&
          (await confirm({
            title: 'Table is full',
            message: `${extractApiErrorMessage(err, 'The table is full')}. Seat them anyway?`,
            confirmLabel: 'Seat anyway',
            variant: 'warning',
          }));
        if (!retry) {
          if (!full) fail(err, 'Could not change the seating');
          return;
        }
        allowOverCapacity = true;
        try {
          await send();
        } catch (again) {
          fail(again, 'Could not change the seating');
          return;
        }
      }
      history.record({
        label,
        undo: () => arrangeSeats.mutateAsync({ floorPlanId, arrangement: before }),
        redo: send,
      });
      setNotice({ text: label, tone: 'info', undo: true });
    },
    [live, floorPlanId, moveSeats, arrangeSeats, confirm, history, fail],
  );

  /** Seat a party at a table (at a seat, when named), or take it off the plan. */
  const seat = useCallback(
    async (guestId: string, tableUid: string | null, seatNumber?: number) => {
      const party = parties.get(guestId);
      if (!live || !party) return;
      const table = tableUid ? live.tables.find((t) => t.uid === tableUid) : null;
      const label = table
        ? `Seated ${party.name} at ${tableTitle(table)}`
        : `Took ${party.name} off the plan`;
      await runMoves([{ guestId, tableUid, ...(seatNumber ? { seatNumber } : {}) }], label);
    },
    [live, parties, runMoves],
  );

  /** Seat one named person of a party, or take them off the plan. */
  const seatMember = useCallback(
    async (guestId: string, memberId: string, tableUid: string | null) => {
      const member = parties.get(guestId)?.members.find((m) => m.id === memberId);
      if (!live || !member) return;
      const table = tableUid ? live.tables.find((t) => t.uid === tableUid) : null;
      await runMoves(
        [{ guestId, partyMemberId: memberId, tableUid }],
        table
          ? `Seated ${member.name} at ${tableTitle(table)}`
          : `Took ${member.name} off the plan`,
      );
    },
    [live, parties, runMoves],
  );

  /** Two seated parties trade places, each taking the other's first seat. */
  const swap = useCallback(
    async (guestId: string, otherId: string) => {
      if (!live) return;
      const firstSeat = (id: string) => {
        for (const table of live.tables) {
          const seats = table.occupied.filter((s) => s.guestId === id).map((s) => s.seatNumber);
          if (seats.length) return { tableUid: table.uid, seatNumber: Math.min(...seats) };
        }
        return null;
      };
      const a = firstSeat(guestId);
      const b = firstSeat(otherId);
      if (!a || !b) return;
      const name = (id: string) => parties.get(id)?.name ?? 'Guest';
      await runMoves(
        [
          { guestId, tableUid: b.tableUid, seatNumber: b.seatNumber },
          { guestId: otherId, tableUid: a.tableUid, seatNumber: a.seatNumber },
        ],
        `Swapped ${name(guestId)} and ${name(otherId)}`,
      );
    },
    [live, parties, runMoves],
  );

  /** Pin a party's seats so auto-assign leaves them, or unpin them. */
  const pin = useCallback(
    async (guestId: string, lockedNow: boolean) => {
      if (!floorPlanId) return;
      const write = (value: boolean) => () =>
        pinSeats.mutateAsync({ floorPlanId, guestIds: [guestId], locked: value });
      try {
        await write(lockedNow)();
      } catch (err) {
        fail(err, 'Could not pin the seats');
        return;
      }
      const label = `${lockedNow ? 'Pinned' : 'Unpinned'} ${parties.get(guestId)?.name ?? 'the party'}`;
      history.record({ label, undo: write(!lockedNow), redo: write(lockedNow) });
      setNotice({ text: label, tone: 'info', undo: true });
    },
    [floorPlanId, pinSeats, parties, history, fail],
  );

  /** Write a whole arrangement (an applied proposal); undoable. */
  const arrange = useCallback(
    async (arrangement: SeatingArrangement, label: string): Promise<boolean> => {
      if (!live || !floorPlanId) return false;
      const everyone = [
        ...new Set([...arrangement.reseat, ...arrangement.placements.map((p) => p.guestId)]),
      ];
      const before = snapshotOf(live, everyone);
      const write = (value: SeatingArrangement) => () =>
        arrangeSeats.mutateAsync({ floorPlanId, arrangement: value });
      try {
        await write(arrangement)();
      } catch (err) {
        fail(err, 'Could not apply the proposal');
        return false;
      }
      history.record({ label, undo: write(before), redo: write(arrangement) });
      setNotice({ text: label, tone: 'info', undo: true });
      return true;
    },
    [live, floorPlanId, arrangeSeats, history, fail],
  );

  const undo = useCallback(async () => {
    try {
      const entry = await history.undo();
      if (entry) setNotice({ text: `Undid: ${entry.label}`, tone: 'info' });
    } catch (err) {
      fail(err, 'Could not undo that');
    }
  }, [history, fail]);

  const redo = useCallback(async () => {
    try {
      const entry = await history.redo();
      if (entry) setNotice({ text: `Redid: ${entry.label}`, tone: 'info' });
    } catch (err) {
      fail(err, 'Could not redo that');
    }
  }, [history, fail]);

  return {
    parties,
    busy,
    history,
    notice,
    setNotice,
    runMoves,
    seat,
    seatMember,
    swap,
    pin,
    arrange,
    undo,
    redo,
  };
}

export type SeatingActions = ReturnType<typeof useSeatingActions>;

/**
 * Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y undo and redo, and Escape puts a picked-up
 * party down. Keys typed into a field, or pressed while a dialog or menu is
 * open, are theirs; `enabled: false` (a proposal on screen, a mode that is
 * off) leaves the keys alone.
 */
export function useSeatingKeys({
  enabled,
  undoEnabled = true,
  onUndo,
  onRedo,
  onEscape,
}: {
  enabled: boolean;
  undoEnabled?: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onEscape: () => void;
}) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || isOverlayOpen()) return;
      if (e.key === 'Escape') {
        onEscape();
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || !undoEnabled) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        onRedo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, undoEnabled, onUndo, onRedo, onEscape]);
}
